/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable sort-imports-es6-autofix/sort-imports-es6 */
import "./chat-interface.scss";
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useEffect, useRef, useState } from "react";
import PromptSection from "./PromptSection";
import AnswerSection from "./AnswerSection";
import { useWebSocket } from "./api/AssistantWebSocket";
import SettingsDialog from "./SettingsDialog";
import { AgenticFeatureRegistry, AgenticSettings } from "./AgenticFeatureRegistry";
import { ChatFile } from "./controls/FileUpload";

import SpeedDial, { Action } from "./controls/SpeedDial";

import MicIcon from "@mui/icons-material/MicRounded";
import ClearIcon from "@mui/icons-material/AutorenewRounded";
import SettingsIcon from "@mui/icons-material/Settings";
import FlashOnIcon from '@mui/icons-material/FlashOn';

import {
  AiAssistant,
  AiMessage,
  AiMessageContent,
  AiRun,
  AiService,
  CatalogService,
  CustomerReference,
  OpenAPI,
  ProductDto,
} from "../../../api-clients/composition";
import { Box, IconButton } from "@mui/material";
import VoiceIO from "./controls/VoiceIO";
import VoiceIORealtime from "./controls/VoiceIORealtime";
import { apiClient } from "../../../framework/api/api.client";

export type UIMessage = {
  role: "you" | "ai";
  text: string;
};

export type PromptResponsePair = {
  aiResponse: string;
  prompt: string;
  promptTypeId: string;
};
/**
 * Display configuration for ChatInterface
 * Controls what UI elements are shown and how the component is styled
 */
export interface ChatDisplayConfig {
  /** Whether to show the prompt/input section at the bottom */
  showPromptSection?: boolean;
  /** Whether to show the header toolbar with settings, clear, etc. */
  showToolbar?: boolean;
  /** Whether to show the voice input controls */
  showVoiceControls?: boolean;
  /** Custom title to display in the header */
  title?: string;
  /** Custom React node to render as the header (replaces default header) */
  customHeader?: React.ReactNode;
  /** Background color for the chat container */
  backgroundColor?: string;
  /** Styling variant */
  variant?: 'default' | 'insight' | 'embedded';
  /** Whether auto-scroll is enabled when new messages arrive */
  autoScroll?: boolean;
  /** Whether the chat is read-only (no input allowed) */
  readOnly?: boolean;
}

interface ChatInterfaceProps {
  applicationKey?: string;
  assistantKey?: string;
  isDrawer?: boolean; // optional
  threadId?: string; // optional - if provided, load existing conversation from this thread
  initialMessages?: AiMessage[]; // optional - initial messages to display (e.g., from DashboardAiContext)
  initialEntityCards?: { messageId: string; entityCards: Record<string, string> }[]; // optional - initial entity cards to populate
  onStatusUpdate?: (status: string) => void; // Callback for status updates
  onIntelligenceCardUpdate?: (intelligenceCard: { headline?: string; byline?: string; fullText?: string }) => void; // Callback for intelligence card updates
  onEntityCardsUpdate?: (entityCards: Record<string, string>) => void; // Callback when entity cards are updated (for a specific message)
  insightMode?: boolean; // optional - if true, hide toolbar and prompt section, disable auto-scroll (read-only insight view)
  /** Display configuration - fine-grained control over UI elements */
  displayConfig?: ChatDisplayConfig;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ 
  applicationKey, 
  assistantKey, 
  isDrawer = true,
  threadId: initialThreadId,
  initialMessages,
  initialEntityCards,
  onStatusUpdate,
  onIntelligenceCardUpdate,
  onEntityCardsUpdate,
  insightMode = false,
  displayConfig,
}) => {
  // Merge displayConfig with insightMode defaults
  // insightMode is a shorthand for common insight display settings
  const effectiveDisplayConfig: ChatDisplayConfig = {
    showPromptSection: displayConfig?.showPromptSection ?? !insightMode,
    showToolbar: displayConfig?.showToolbar ?? !insightMode,
    showVoiceControls: displayConfig?.showVoiceControls ?? !insightMode,
    autoScroll: displayConfig?.autoScroll ?? !insightMode,
    readOnly: displayConfig?.readOnly ?? insightMode,
    title: displayConfig?.title,
    customHeader: displayConfig?.customHeader,
    backgroundColor: displayConfig?.backgroundColor,
    variant: displayConfig?.variant ?? (insightMode ? 'insight' : 'default'),
  };
  // Log props on mount/render for debugging
  useEffect(() => {
    console.log(`📊 [ChatInterface] Component rendered with props:`, {
      insightMode,
      hasInitialMessages: !!initialMessages,
      initialMessagesCount: initialMessages?.length || 0,
      initialMessagesIds: initialMessages?.map(m => m.id).join(', ') || 'none',
      hasInitialEntityCards: !!initialEntityCards,
      initialEntityCardsCount: initialEntityCards?.length || 0,
      initialThreadId,
      applicationKey,
    });
  }, []); // Only log on mount
  
  // const [listening, setListening] = useState<boolean>(false);
  const theme = useTheme();
  const loadingRef = useRef(false); // Use a ref to store the current loading state
  // In insight mode, initialize directly with initialMessages to avoid any clearing/resetting
  const [messages, setMessages] = useState<AiMessage[]>(
    insightMode && initialMessages && initialMessages.length > 0 ? initialMessages : []
  );
  const [responseMessages, setResponseMessages] = useState<AiMessage[]>([]);
  const [messagesPreview, setMessagesPreview] = useState<AiMessage[]>([]);
  const [useAvatar, setUseAvatar] = useState<boolean>(false);
  const [run, setRun] = useState<AiRun>();
  const runRef = useRef(run);
  const [assistant, setAssistant] = useState<AiAssistant>();
  const assistantRef = useRef(assistant);
  const [shouldPoll, setShouldPoll] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [products, setProducts] = useState<Map<string, ProductDto>>(new Map());
  const [customers, setCustomers] = useState<Map<string, CustomerReference>>(
    new Map()
  );
  const [runStatus, setRunStatus] = useState<string>("");
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [agenticSettings, setAgenticSettings] = useState<AgenticSettings>({
    agentId: '',
    applicationKey: applicationKey || '',
    assistantKey: assistantKey || '',
  });
  const [voiceMode, setVoiceMode] = useState<'traditional' | 'realtime'>('traditional');
  
  // Store entity cards by message ID for hover card display
  // In insight mode, initialize directly with initialEntityCards to avoid any clearing/resetting
  const [entityCardsByMessage, setEntityCardsByMessage] = useState<Map<string, Record<string, string>>>(() => {
    if (insightMode && initialEntityCards && initialEntityCards.length > 0) {
      const map = new Map<string, Record<string, string>>();
      initialEntityCards.forEach(({ messageId, entityCards }) => {
        if (messageId && entityCards) {
          map.set(messageId, entityCards);
        }
      });
      console.log(`📊 [ChatInterface] Initialized entityCardsByMessage directly with ${map.size} entries (insight mode)`);
      return map;
    }
    return new Map();
  });
  
  // Track component visibility to prevent sending messages when hidden
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const componentRef = useRef<HTMLDivElement>(null);

  // Ensure `loading` state stays in sync by using a ref
  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    loadingRef.current = loading;
  }, [loading]);

  // Track component visibility using IntersectionObserver
  useEffect(() => {
    if (!componentRef.current) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting && entry.intersectionRatio > 0;
        console.log(`👁️ [ChatInterface] Visibility changed: ${visible ? 'VISIBLE' : 'HIDDEN'}`);
        setIsVisible(visible);
      },
      { threshold: 0.1 } // Consider visible if at least 10% is showing
    );
    
    observer.observe(componentRef.current);
    
    return () => observer.disconnect();
  }, []);

  const onRunStarted = useCallback((message) => {
    //setLoading(true);
    setLoading(() => {
      loadingRef.current = true; // Keep the ref updated
      return true;

    });
  }, []);

  const onRunCompleted = useCallback((message) => {
    setLoading(() => false); // Functional update ensures React always uses the latest state
    
    // Notify feature providers
    AgenticFeatureRegistry.notifyRunCompleted(messages);
    
    // CRITICAL: Skip server loading in insightMode - use initialMessages only
    if (insightMode) {
      console.log(`🚫 [ChatInterface] onRunCompleted: Skipping loadMessages in insightMode`);
      return;
    }
    
    // Reload messages from API to get final versions with metadata (intelligenceCard, etc.)
    // This ensures we have the complete message data including metadata that wasn't in WebSocket deltas
    // Load only messages for the current run (false) to avoid loading unrelated messages
    console.log('📥 [ChatInterface] Run completed - reloading messages with metadata');
    const currentRun = runRef.current;
    if (currentRun?.id) {
      loadMessages(false).catch((error) => {
        console.error('❌ [ChatInterface] Error reloading messages after run completion:', error);
      });
    } else {
      console.warn('⚠️ [ChatInterface] No current run ID available for reloading messages');
    }
  }, [messages, insightMode]);

  const onRunStatus = useCallback((message) => {
    console.log('Chat InterfaceRun Status', message);
    setRunStatus(message);
    // Call external callback if provided
    if (onStatusUpdate) {
      onStatusUpdate(message);
    }
  }, [onStatusUpdate]);

  const onEntityCards = useCallback((data: any) => {
    console.log('💾 [ChatInterface] onEntityCards callback invoked');
    console.log('   Timestamp:', new Date().toISOString());
    console.log('   Message ID:', data?.messageId);
    console.log('   Cards received:', Object.keys(data?.entityCards || {}).join(', '));
    console.log('   🔗 DEBUG: Full entityCards data:', JSON.stringify(data?.entityCards, null, 2));
    console.log('   🔗 DEBUG: messageId from event:', data?.messageId);
    console.log('   🔗 DEBUG: Current messages in state:', messages.map(m => ({ id: m.id, role: m.role })));
    
    if (!data || !data.messageId) {
      console.error('❌ [ChatInterface] Invalid data received in onEntityCards:', data);
      return;
    }
    
    if (!data.entityCards || Object.keys(data.entityCards).length === 0) {
      console.warn('⚠️ [ChatInterface] Entity cards object is empty or missing');
    }
    
    setEntityCardsByMessage(prev => {
      const newMap = new Map(prev);
      console.log(`   🔗 DEBUG: Before update - existing messageIds: ${Array.from(prev.keys()).join(', ')}`);
      newMap.set(data.messageId, data.entityCards);
      console.log(`   Total messages with cards: ${newMap.size}`);
      console.log(`   🔗 DEBUG: All stored messageIds: ${Array.from(newMap.keys()).join(', ')}`);
      console.log(`   🔗 DEBUG: Cards for this messageId: ${Object.keys(newMap.get(data.messageId) || {}).join(', ')}`);
      console.log('✅ [ChatInterface] Entity cards stored successfully');
      
      // Call onEntityCardsUpdate callback if provided (for headline/byline extraction)
      if (onEntityCardsUpdate && data.entityCards) {
        onEntityCardsUpdate(data.entityCards);
      }
      
      return newMap;
    });
  }, [messages, onEntityCardsUpdate]);
  
  // Log when callback changes to verify it's being recreated
  useEffect(() => {
    console.log('🔄 [ChatInterface] onEntityCards callback reference changed');
    console.log('   Current messages count:', messages.length);
  }, [onEntityCards]);

  const { socketMessages, entityCardEvents, clearSocketMessages } = useWebSocket({
    onRunStarted,
    onRunCompleted,
    onRunStatus,
    onEntityCards,
  }); // Use the WebSocket hook
  
  // Log entity card events received by the hook
  useEffect(() => {
    if (entityCardEvents && entityCardEvents.length > 0) {
      console.log(`📊 [ChatInterface] Total entity card events from hook: ${entityCardEvents.length}`);
      entityCardEvents.forEach((event, index) => {
        console.log(`   Event ${index + 1}: messageId=${event.messageId}, cards=${Object.keys(event.entityCards || {}).join(', ')}`);
      });
    }
  }, [entityCardEvents]);

  // Track if we've already processed initial entity cards to prevent redundant processing
  const processedInitialEntityCardsRef = useRef<string | null>(null);
  
  // Populate entity cards from initialEntityCards prop when component mounts
  // In insight mode, this is already done via direct initialization - this effect is for non-insight mode
  useEffect(() => {
    // In insight mode, we already initialized directly - skip this effect
    if (insightMode) {
      console.log(`📊 [ChatInterface] Insight mode: Entity cards already initialized directly, skipping effect`);
      return;
    }
    
    if (!initialEntityCards || initialEntityCards.length === 0) return;
    
    // Create a stable key from the first entity card's messageId to track if we've already processed this set
    const entityCardsKey = initialEntityCards.map(ec => ec.messageId).join('|');
    if (processedInitialEntityCardsRef.current === entityCardsKey) {
      // Already processed these exact entity cards, skip
      return;
    }
    processedInitialEntityCardsRef.current = entityCardsKey;
    
    console.log(`📊 [ChatInterface] Populating entity cards from initialEntityCards prop (${initialEntityCards.length} sets)`);
    
    // Extract expected message IDs from initialEntityCards
    const expectedMessageIds = initialEntityCards
      .filter(({ messageId }) => messageId)
      .map(({ messageId }) => messageId);
    
    // CRITICAL: Always populate entity cards FIRST, regardless of messages state
    setEntityCardsByMessage(prev => {
      const newMap = new Map(prev);
      let addedCount = 0;
      
      initialEntityCards.forEach(({ messageId, entityCards }) => {
        if (messageId && entityCards && Object.keys(entityCards).length > 0) {
          const existing = newMap.get(messageId);
          // Merge with any existing cards
          newMap.set(messageId, { ...existing, ...entityCards });
          if (!existing) addedCount++;
        }
      });
      
      if (addedCount > 0) {
        console.log(`   ✅ [ChatInterface] Added ${addedCount} entity card set(s). Total: ${newMap.size}`);
      }
      
      return newMap;
    });
    
    // Check if we have messages matching the expected IDs
    const hasMatchingMessagesInState = expectedMessageIds.some(id => 
      messages.some(m => m.id === id)
    );
    const hasMatchingMessagesInInitial = initialMessages && initialMessages.length > 0 && 
      expectedMessageIds.some(id => 
        initialMessages.some(m => m.id === id)
      );
    const hasMatchingMessages = hasMatchingMessagesInState || hasMatchingMessagesInInitial;
      
      // Only create placeholder messages if we don't have matching messages
      // Only create placeholder messages if no matching messages exist and no initialMessages will provide them
      if (!hasMatchingMessages && !(initialMessages && hasMatchingMessagesInInitial)) {
        console.log(`   📊 [ChatInterface] Creating placeholder messages for entity cards`);
        setMessages(prevMessages => {
          const messagesMap = new Map(prevMessages.map(m => [m.id, m]));
          let messagesUpdated = false;
          
          initialEntityCards.forEach(({ messageId, entityCards }) => {
            if (messageId && entityCards && Object.keys(entityCards).length > 0) {
              if (!messagesMap.has(messageId)) {
                // Create a placeholder assistant message with entity references
                const entityRefKeys = Object.keys(entityCards);
                const messageText = entityRefKeys.map(key => `\${${key}}`).join('\n');
                
                const placeholderMessage: AiMessage = {
                  id: messageId,
                  role: 'assistant',
                  content: [{ type: 'text', text: messageText || 'AI Insight', fileReferences: [] }],
                  createdAt: Date.now(),
                  assistantId: assistantRef.current?.id || '',
                  threadId: assistantRef.current?.threads[0]?.id || initialThreadId || '',
                  runId: '',
                  parentAssistantId: '',
                  personId: '',
                  metaData: JSON.stringify({ internal: true, placeholder: true }),
                  fileReferences: [],
                };
                
                messagesMap.set(messageId, placeholderMessage);
                messagesUpdated = true;
              }
            }
          });
          
          if (messagesUpdated) {
            console.log(`   ✅ [ChatInterface] Created placeholder message(s)`);
            return Array.from(messagesMap.values()).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
          }
          
          return prevMessages;
        });
      }
    // Note: Using stable initialEntityCards reference and initialThreadId as main dependencies
    // Removed messages.length and isVisible to prevent re-run loops
  }, [insightMode, initialEntityCards, initialMessages, initialThreadId]);

  useState<PromptResponsePair>();

  const saveState = () => {
    const stateToSave = {
      assistant,
      customers: Array.from(customers.entries()),
      messages,
      products: Array.from(products.entries()),
      run: run,
      useAvatar,
    };

    sessionStorage.setItem("chatInterfaceState", JSON.stringify(stateToSave));
  };

  const restoreState = () => {
    let savedState = sessionStorage.getItem("chatInterfaceState");

    if (!savedState) savedState = "{}";

    const { assistant, customers, messages, products, run, useAvatar } =
      JSON.parse(savedState);

    if (useAvatar) setUseAvatar(useAvatar);

    if (products) {
      setProducts(new Map(products));
    }

    if (customers) setCustomers(new Map(customers));

    if (assistant) {
      setAssistant({ ...assistant });
    }

    if (run) {
      setRun(run);
    }

    if (messages) {
      setMessages(messages);
    }

    if (!assistant || !assistant.id) {
      createAssistant();
    }
  };

  const formatActions = (text: string): string => {
    const actionsMap = new Map<string, string>(
      actions.map((action) => [action.id, action.label])
    );
    const regex = /action_[a-zA-Z0-9_]+/g;

    return text?.replace(regex, (match) => {
      // Check if the matched command exists in the map
      if (actionsMap.has(match)) {
        // Replace with value from the map
        return actionsMap.get(match)!;
      } else {
        // If not found in the map, keep the original text
        return match;
      }
    });
  };

  const processMessageReferences = () => {
    type Reference = { key: string; value: string };

    if (!messages) {
      return;
    }

    messages.forEach((message) => {
      if (message.metaData) {
        const references: Reference[] = JSON.parse(message.metaData);
        if (references && references.length) {
          references.forEach((reference) => {
            const key = reference.key;
            const value = reference.value;
            if (key === "product") {
              loadProductDetails(value);
            } else if (key === "customer") loadCustomerDetails(value);
          });
        }
      }
    });
  };

  // Sync WebSocket messages with component state
  useEffect(() => {
    streamMessage(socketMessages as [any]);
  }, [socketMessages]);

  const streamMessage = (socketMessages: [any]) => {
    if (!socketMessages || !socketMessages.length) {
      console.log(
        "streamMessage, returning early - no socket messages",
        socketMessages?.length
      );
      return;
    }
    setLoading(false);

    const socketMsg = socketMessages[socketMessages.length - 1];
    
    console.log(`🔗 DEBUG streamMessage: socketMsg.messageId = "${socketMsg?.messageId}"`);
    console.log(`🔗 DEBUG streamMessage: socketMsg.delta length = ${socketMsg?.delta?.length || 0}`);
    
    // Use functional update to ensure we have the latest state and trigger re-render
    setMessages(prevMessages => {
      const lastMessage = prevMessages?.length ? prevMessages[prevMessages.length - 1] : null;

      // If no messages exist yet, or last message is from user, create a new assistant message
      if (!lastMessage || lastMessage.role !== "assistant") {
        const messageId = socketMsg.messageId || `msg_${Date.now()}`;
        const newAssistantMessage: AiMessage = {
          assistantId: assistant?.id || "",
          content: [
            {
              fileReferences: [],
              text: socketMsg?.delta || "",
              type: "text",
            },
          ],
          createdAt: Date.now(),
          fileReferences: [],
          id: messageId,
          metaData: "",
          parentAssistantId: "",
          personId: "",
          role: "assistant",
          runId: run?.id || "",
          threadId: assistant?.threads?.[0]?.id || "",
        };

        console.log(`📝 Creating new assistant message with ID: ${newAssistantMessage.id}`);
        console.log(`🔗 DEBUG: Message ID from socket: "${socketMsg.messageId}"`);
        console.log(`🔗 DEBUG: Final message ID: "${newAssistantMessage.id}"`);
        return [...(prevMessages || []), newAssistantMessage];
      } else {
        // Append to existing assistant message - create new object to trigger re-render
        const updatedMessages = prevMessages.map((msg, idx) => {
          if (idx === prevMessages.length - 1 && msg.role === "assistant") {
            // Update message ID if provided by backend
            const newId = (socketMsg.messageId && msg.id !== socketMsg.messageId) 
              ? socketMsg.messageId 
              : msg.id;
            
            if (newId !== msg.id) {
              console.log(`📝 Updating message ID from ${msg.id} to ${newId}`);
              console.log(`🔗 DEBUG: Old ID: "${msg.id}", New ID: "${newId}", Socket messageId: "${socketMsg.messageId}"`);
            } else {
              console.log(`🔗 DEBUG: Message ID unchanged: "${msg.id}" (socket messageId: "${socketMsg.messageId}")`);
            }
            
            // Check if delta would create duplicate content (e.g., if delta contains the entire existing text)
            const existingText = msg.content[0]?.text || "";
            const delta = socketMsg?.delta || "";
            let textToAppend = delta;
            
            // CRITICAL: Check if delta is already contained at the end of existing text (duplicate delta)
            if (existingText && delta) {
              // Case 1: Delta is exactly the same as existing text (full duplicate)
              if (delta === existingText) {
                console.warn(`⚠️ [ChatInterface] Detected exact duplicate delta - delta matches entire existing text, skipping append`);
                textToAppend = ""; // Don't append anything
              }
              // Case 2: Delta is already at the end of existing text (duplicate append)
              else if (existingText.endsWith(delta) && delta.length > 10) {
                console.warn(`⚠️ [ChatInterface] Detected duplicate delta at end - delta already appended, skipping`);
                console.warn(`   Existing text ends with: "${existingText.substring(existingText.length - Math.min(50, delta.length))}"`);
                console.warn(`   Delta: "${delta.substring(0, Math.min(50, delta.length))}"`);
                textToAppend = ""; // Don't append anything
              }
              // Case 3: Delta contains the full existing text (streaming service re-sends full message)
              else if (delta.length > existingText.length * 0.8 && delta.startsWith(existingText)) {
                console.warn(`⚠️ [ChatInterface] Detected duplicate content in delta - delta starts with full existing text`);
                console.warn(`   Existing length: ${existingText.length}, Delta length: ${delta.length}`);
                // Use only the delta (which should be the complete message)
                textToAppend = delta;
                // But we need to replace, not append
                return {
                  ...msg,
                  id: newId,
                  content: [
                    {
                      ...msg.content[0],
                      text: delta, // Replace with delta
                    },
                    ...msg.content.slice(1),
                  ],
                  metaData: msg.metaData || "",
                };
              }
              // Case 4: Delta contains existing text but is much longer (partial duplicate)
              else if (delta.includes(existingText) && delta.length > existingText.length * 1.5) {
                console.warn(`⚠️ [ChatInterface] Detected duplicate content in delta - delta contains existing text`);
                // Extract only the new part
                const existingIndex = delta.indexOf(existingText);
                if (existingIndex === 0) {
                  textToAppend = delta; // Delta starts with existing, use delta as-is (replace)
                  return {
                    ...msg,
                    id: newId,
                    content: [
                      {
                        ...msg.content[0],
                        text: delta, // Replace with delta
                      },
                      ...msg.content.slice(1),
                    ],
                    metaData: msg.metaData || "",
                  };
                } else {
                  textToAppend = delta.substring(existingIndex + existingText.length);
                }
              }
            }
            
            // If nothing to append, return unchanged message
            if (!textToAppend) {
              console.log(`⏭️ [ChatInterface] Skipping append - no new content`);
              return msg;
            }
            
            let newText = existingText + textToAppend;
            
            // Check if the final text contains exact duplication (same text appears twice consecutively)
            // This handles cases where the LLM returns the same content twice
            if (newText.length > 100) {
              const midPoint = Math.floor(newText.length / 2);
              const firstHalf = newText.substring(0, midPoint);
              const secondHalf = newText.substring(midPoint);
              
              // Check if first half appears at the start of second half (exact duplication)
              if (secondHalf.startsWith(firstHalf) && firstHalf.length > 50) {
                console.warn(`⚠️ [ChatInterface] Detected exact duplication within message text - removing duplicate`);
                console.warn(`   Text length: ${newText.length}, First half length: ${firstHalf.length}`);
                // Keep only the first occurrence
                newText = firstHalf + secondHalf.substring(firstHalf.length);
              }
            }
            
            return {
              ...msg,
              id: newId,
              content: [
                {
                  ...msg.content[0],
                  text: newText,
                },
                ...msg.content.slice(1),
              ],
              // Preserve existing metaData if present (will be updated when loadMessages is called after completion)
              metaData: msg.metaData || "",
            };
          }
          return msg;
        });
        
        return updatedMessages;
      }
    });
  };

  // Only restore state if we don't have an initial threadId (which means we want to load from that thread instead)
  useEffect(() => {
    if (!initialThreadId) {
      restoreState();
    }
  }, []); // Call this in useEffect to run once on component mount

  useEffect(saveState, [messages, run]);

  useEffect(processMessageReferences, [messages]);

  // Extract intelligenceCard from messages whenever they change
  useEffect(() => {
    if (!messages || messages.length === 0 || !onIntelligenceCardUpdate) return;
    
    // Find the last assistant message with intelligenceCard in metadata
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message.role === 'assistant' && message.metaData) {
        try {
          const metadata = JSON.parse(message.metaData);
          if (metadata.intelligenceCard) {
            const intelligenceCard = metadata.intelligenceCard;
            console.log(`📊 [ChatInterface] Extracted intelligenceCard from message ${message.id}:`, {
              headline: intelligenceCard.headline,
              byline: intelligenceCard.byline,
              hasFullText: !!intelligenceCard.fullText,
            });
            
            if (intelligenceCard.headline || intelligenceCard.byline) {
              console.log(`✅ [ChatInterface] UI layer received Headline: "${intelligenceCard.headline || 'N/A'}"`);
              console.log(`✅ [ChatInterface] UI layer received Byline: "${intelligenceCard.byline || 'N/A'}"`);
              
              // Call callback if provided
              if (onIntelligenceCardUpdate) {
                onIntelligenceCardUpdate(intelligenceCard);
              }
              
              // Also emit global event for components that can't directly access ChatInterface
              window.dispatchEvent(new CustomEvent('intelligenceCardUpdate', {
                detail: { intelligenceCard, threadId: message.threadId, messageId: message.id }
              }));
            }
            return; // Found and notified, exit
          }
        } catch (error) {
          // Continue to next message if metadata parsing fails
        }
      }
    }
  }, [messages, onIntelligenceCardUpdate]);

  // Use initialThreadId as the effective thread ID
  const effectiveThreadId = initialThreadId;

  // Track the last loaded threadId to avoid unnecessary reloads
  const lastLoadedThreadIdRef = useRef<string | null>(null);
  // Track if we're currently loading to prevent concurrent loads
  const isLoadingThreadRef = useRef<boolean>(false);
  
  // In insight mode, messages are initialized directly in useState - no complex effects needed
  // This effect just logs for debugging and marks the thread as "loaded" to prevent reload attempts
  useEffect(() => {
    if (insightMode) {
      console.log(`📊 [ChatInterface] Insight mode active - messages initialized directly, no server loading`);
      console.log(`   📊 [ChatInterface] Current messages count: ${messages.length}`);
      console.log(`   📊 [ChatInterface] Message IDs: ${messages.map(m => m.id).join(', ') || 'none'}`);
      console.log(`   📊 [ChatInterface] Entity cards count: ${entityCardsByMessage.size}`);
      
      // Mark thread as loaded to prevent any reload attempts
      if (initialThreadId) {
        lastLoadedThreadIdRef.current = initialThreadId;
      }
    }
  }, [insightMode, initialThreadId]); // Deliberately minimal dependencies - just log on mount

  // Load existing thread if effectiveThreadId is provided
  useEffect(() => {
    const loadExistingThread = async () => {
      // CRITICAL: Skip ALL server loading if in insightMode
      // In insight mode, messages are already initialized directly - no server calls needed
      if (insightMode) {
        console.log(`📊 [ChatInterface] Insight mode: Skipping loadExistingThread entirely`);
        return;
      }
      
      if (!effectiveThreadId) return;
      
      // CRITICAL: Check if already loaded FIRST, before any other logic
      // This prevents the effect from running multiple times for the same threadId
      if (lastLoadedThreadIdRef.current === effectiveThreadId) {
        console.log(`✅ [ChatInterface] Thread ${effectiveThreadId} already loaded, skipping reload`);
        return;
      }
      
      // CRITICAL: Check if we're already loading this thread to prevent concurrent loads
      if (isLoadingThreadRef.current) {
        console.log(`⏳ [ChatInterface] Thread ${effectiveThreadId} is already being loaded, skipping concurrent load`);
        return;
      }
      
      // Use refs to get current values to avoid stale closures
      const currentAssistant = assistantRef.current;
      const currentMessages = messages; // Get from closure, but we'll check refs too
      const currentThreadId = currentAssistant?.threads?.[0]?.id;
      const hasMessagesForThread = currentMessages.length > 0 && currentMessages.some(m => m.threadId === effectiveThreadId);
      const isSameThread = currentThreadId === effectiveThreadId;
      
      // CRITICAL: If messages were just cleared (empty messages and lastLoadedThreadIdRef was cleared),
      // and we're trying to load a thread that doesn't match the current assistant's thread,
      // skip loading to prevent reloading old messages after a clear operation
      if (currentMessages.length === 0 && lastLoadedThreadIdRef.current === null && currentAssistant?.id && currentThreadId !== effectiveThreadId) {
        console.log(`🚫 [ChatInterface] Messages were cleared and threadId mismatch - skipping load to prevent reloading old messages`);
        console.log(`   Current assistant threadId: ${currentThreadId}, Requested threadId: ${effectiveThreadId}`);
        return;
      }
      
      console.log(`📥 [ChatInterface] Loading existing thread: ${effectiveThreadId}`);
      console.log(`📥 [ChatInterface] Current threadId: ${currentThreadId}`);
      console.log(`📥 [ChatInterface] Current messages count: ${currentMessages.length}`);
      console.log(`📥 [ChatInterface] Has messages for this thread: ${hasMessagesForThread}`);
      console.log(`📥 [ChatInterface] Is same thread: ${isSameThread}`);
      
      // If we already have messages for this thread and assistant is ready, skip reload
      if (isSameThread && hasMessagesForThread && currentAssistant?.id) {
        console.log(`✅ [ChatInterface] Already have messages for this thread, skipping reload`);
        lastLoadedThreadIdRef.current = effectiveThreadId;
        return;
      }
      
      // Mark as loading to prevent concurrent loads
      isLoadingThreadRef.current = true;
      
      // Check if we have initialMessages prop (e.g., from DashboardAiContext)
      if (initialMessages && initialMessages.length > 0) {
        const initialMessagesForThread = initialMessages.filter(m => m.threadId === effectiveThreadId);
        if (initialMessagesForThread.length > 0) {
          console.log(`✅ [ChatInterface] Found ${initialMessagesForThread.length} initial messages for threadId: ${effectiveThreadId}`);
          setMessages(initialMessagesForThread);
          lastLoadedThreadIdRef.current = effectiveThreadId;
          
          // Still need to get assistant
          if ((applicationKey || assistantKey) && (!currentAssistant?.id || !isSameThread)) {
            const currentSettings = AgenticFeatureRegistry.getSettings();
            const appKey = currentSettings.applicationKey || applicationKey || "";
            const assKey = currentSettings.assistantKey || assistantKey || "";
            
            const assistantRequest: any = {
              applicationKey: appKey,
              assistantKey: assKey,
            };
            
            const employeeAssistant = await AiService.aiControllerGetAssistant(assistantRequest);
            
            if (employeeAssistant.threads && employeeAssistant.threads.length > 0) {
              employeeAssistant.threads[0].id = effectiveThreadId;
            }
            
            setAssistant(employeeAssistant);
            console.log(`✅ [ChatInterface] Assistant ready (loaded from initialMessages)`);
          }
          lastLoadedThreadIdRef.current = effectiveThreadId;
          isLoadingThreadRef.current = false;
          return; // Skip API call since we have initial messages
        }
      }
      
      // Check sessionStorage for messages with this threadId before calling API
      try {
        const savedState = sessionStorage.getItem("chatInterfaceState");
        if (savedState) {
          const parsed = JSON.parse(savedState);
          if (parsed.messages && Array.isArray(parsed.messages)) {
            const savedMessagesForThread = parsed.messages.filter((m: any) => m.threadId === effectiveThreadId);
            if (savedMessagesForThread.length > 0) {
              console.log(`✅ [ChatInterface] Found ${savedMessagesForThread.length} messages in sessionStorage for threadId: ${effectiveThreadId}`);
              setMessages(savedMessagesForThread);
              lastLoadedThreadIdRef.current = effectiveThreadId;
              
              // Still need to get assistant, but skip API call for messages
              if ((applicationKey || assistantKey) && (!currentAssistant?.id || !isSameThread)) {
                const currentSettings = AgenticFeatureRegistry.getSettings();
                const appKey = currentSettings.applicationKey || applicationKey || "";
                const assKey = currentSettings.assistantKey || assistantKey || "";
                
                const assistantRequest: any = {
                  applicationKey: appKey,
                  assistantKey: assKey,
                };
                
                const employeeAssistant = await AiService.aiControllerGetAssistant(assistantRequest);
                
                if (employeeAssistant.threads && employeeAssistant.threads.length > 0) {
                  employeeAssistant.threads[0].id = effectiveThreadId;
                }
                
                setAssistant(employeeAssistant);
                console.log(`✅ [ChatInterface] Assistant ready (loaded from sessionStorage)`);
              }
              lastLoadedThreadIdRef.current = effectiveThreadId;
              isLoadingThreadRef.current = false;
              return; // Skip API call since we have messages from sessionStorage
            } else {
              console.log(`ℹ️ [ChatInterface] No messages in sessionStorage for threadId: ${effectiveThreadId} (found ${parsed.messages.length} total messages)`);
            }
          }
        }
      } catch (error) {
        console.warn(`⚠️ [ChatInterface] Failed to check sessionStorage:`, error);
      }
      
      // Also check if we have socketMessages that match this threadId
      if (socketMessages && socketMessages.length > 0) {
        const socketMessagesForThread = socketMessages.filter((msg: any) => {
          // Check if message has threadId matching, or if it's from the current assistant's thread
          return msg.threadId === effectiveThreadId || 
                 (currentAssistant?.threads?.[0]?.id === effectiveThreadId && msg.threadId === currentAssistant?.threads?.[0]?.id);
        });
        
        if (socketMessagesForThread.length > 0) {
          console.log(`✅ [ChatInterface] Found ${socketMessagesForThread.length} messages in WebSocket state for threadId: ${effectiveThreadId}`);
          // Process socket messages into AiMessage format
          const processedMessages: AiMessage[] = socketMessagesForThread.map((socketMsg: any) => ({
            id: socketMsg.messageId || `msg_${Date.now()}`,
            threadId: effectiveThreadId,
            role: 'assistant',
            content: [{ type: 'text', text: socketMsg.fullResponse || socketMsg.delta || '', fileReferences: [] }],
            metaData: socketMsg.metaData || '',
            createdAt: Date.now(),
            assistantId: currentAssistant?.id || '',
            parentAssistantId: currentAssistant?.id || '',
            personId: '',
            runId: socketMsg.runId || '',
            fileReferences: [],
          }));
          
          setMessages(processedMessages);
          lastLoadedThreadIdRef.current = effectiveThreadId;
          
          // Still need to get assistant
          if ((applicationKey || assistantKey) && (!currentAssistant?.id || !isSameThread)) {
            const currentSettings = AgenticFeatureRegistry.getSettings();
            const appKey = currentSettings.applicationKey || applicationKey || "";
            const assKey = currentSettings.assistantKey || assistantKey || "";
            
            const assistantRequest: any = {
              applicationKey: appKey,
              assistantKey: assKey,
            };
            
            const employeeAssistant = await AiService.aiControllerGetAssistant(assistantRequest);
            
            if (employeeAssistant.threads && employeeAssistant.threads.length > 0) {
              employeeAssistant.threads[0].id = effectiveThreadId;
            }
            
            setAssistant(employeeAssistant);
            console.log(`✅ [ChatInterface] Assistant ready (loaded from WebSocket state)`);
          }
          isLoadingThreadRef.current = false;
          return; // Skip API call since we have messages from WebSocket
        }
      }
      
      setLoading(true);
      
      try {
        // Load messages from the specified thread
        const threadMessages = await AiService.aiControllerLoadMessages({
          threadId: effectiveThreadId,
        });
        
        console.log(`📥 [ChatInterface] Raw API response: ${threadMessages.length} messages`);
        console.log(`📥 [ChatInterface] Raw messages:`, threadMessages.map(m => ({ id: m.id, role: m.role, content: (m as any).content?.substring(0, 50) || (m as any).text?.substring(0, 50) })));
        
        // Reverse to get chronological order (API returns newest first)
        const orderedMessages = threadMessages.reverse();
        
        // Filter out internal/context messages from UI display
        const visibleMessages = orderedMessages.filter((message) => {
          if (!message.metaData) return true;
          try {
            const metadata = JSON.parse(message.metaData);
            return !metadata.internal; // Hide messages marked as internal
          } catch {
            return true; // If can't parse, show it
          }
        });
        
        console.log(`📝 [ChatInterface] Loaded ${visibleMessages.length} visible messages from existing thread`);
        console.log(`📝 [ChatInterface] Visible messages:`, visibleMessages.map(m => ({ id: m.id, role: m.role, content: (m as any).content?.substring(0, 50) || (m as any).text?.substring(0, 50) })));
        
        // Only update messages if we got some from the server
        // If server returns empty but we already have messages, preserve existing messages
        if (visibleMessages.length > 0) {
          console.log(`✅ [ChatInterface] Setting ${visibleMessages.length} messages from server`);
          setMessages(visibleMessages);
        } else if (messages.length > 0) {
          console.log(`⚠️ [ChatInterface] Server returned 0 messages but we have ${messages.length} in state - preserving existing messages`);
          // Don't clear existing messages if server returns empty
        } else {
          console.log(`ℹ️ [ChatInterface] No messages from server and no messages in state`);
          setMessages([]);
        }
        setLoading(false);
        lastLoadedThreadIdRef.current = effectiveThreadId;
        
        // Only create/get assistant if we don't already have one for this thread
        // Use currentAssistant from ref to avoid stale closure
        const finalAssistant = assistantRef.current;
        if ((applicationKey || assistantKey) && (!finalAssistant?.id || finalAssistant?.threads?.[0]?.id !== effectiveThreadId)) {
          const currentSettings = AgenticFeatureRegistry.getSettings();
          const appKey = currentSettings.applicationKey || applicationKey || "";
          const assKey = currentSettings.assistantKey || assistantKey || "";
          
          const assistantRequest: any = {
            applicationKey: appKey,
            assistantKey: assKey,
          };
          
          const employeeAssistant = await AiService.aiControllerGetAssistant(assistantRequest);
          
          // If the assistant has a different thread, update it to use the provided threadId
          // This enables continuing the conversation in the same thread
          if (employeeAssistant.threads && employeeAssistant.threads.length > 0) {
            console.log(`📝 [ChatInterface] Assistant thread before update: ${employeeAssistant.threads[0].id}`);
            employeeAssistant.threads[0].id = effectiveThreadId;
            console.log(`📝 [ChatInterface] Assistant thread after update: ${employeeAssistant.threads[0].id}`);
          }
          
          setAssistant(employeeAssistant);
          console.log(`✅ [ChatInterface] Assistant ready for existing thread`);
        } else {
          console.log(`✅ [ChatInterface] Assistant already ready for this thread, skipping getAssistant call`);
        }
      } catch (error) {
        console.error(`❌ [ChatInterface] Failed to load existing thread:`, error);
        setLoading(false);
      } finally {
        // Always reset loading flag, even on error
        isLoadingThreadRef.current = false;
      }
    };
    
    loadExistingThread();
    // Note: socketMessages is intentionally NOT in dependencies - it changes frequently and we only check it as a fallback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveThreadId, applicationKey, assistantKey, initialMessages, insightMode]);

  // Initialize registry settings and subscribe to changes
  useEffect(() => {
    const currentSettings = AgenticFeatureRegistry.getSettings();
    if (!currentSettings.applicationKey && applicationKey) {
      AgenticFeatureRegistry.updateSettings({ 
        applicationKey,
        assistantKey: assistantKey || ''
      });
    }
    
    setAgenticSettings(AgenticFeatureRegistry.getSettings());
    
    // Subscribe to registry changes so we react to external updates
    const unsubscribe = AgenticFeatureRegistry.subscribeToSettingsChanges((newSettings) => {
      console.log('🔔 [ChatInterface] Registry settings changed, updating local state:', newSettings);
      setAgenticSettings(newSettings);
    });
    
    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [applicationKey, assistantKey]);

  // Watch for focus changes and send context if assistant is already active AND component is visible
  useEffect(() => {
    const settings = AgenticFeatureRegistry.getSettings();
    const focusKey = settings.focusKey;
    
    if (!focusKey) {
      console.log(`ℹ️ [ChatInterface] Focus cleared or not set`);
      return;
    }
    
    if (!assistant?.id) {
      // No assistant yet - focus will be handled when assistant is created
      console.log(`ℹ️ [ChatInterface] Focus changed to ${focusKey}, but assistant not ready yet (will send context when assistant loads)`);
      return;
    }
    
    // ⚠️ Only send context if component is visible!
    if (!isVisible) {
      console.log(`🚫 [ChatInterface] Focus changed to ${focusKey}, but component is HIDDEN - skipping context message`);
      return;
    }
    
    // Assistant exists and focus changed - send new context
    console.log(`🎯 [ChatInterface] Focus changed while assistant active AND visible. Sending context for: ${focusKey}`);
    
    const focusPrompt = AgenticFeatureRegistry.getPromptForFocus(focusKey);
    
    if (focusPrompt) {
      console.log(`📤 [ChatInterface] Sending focus context update`);
      console.log(`📝 [ChatInterface] Focus prompt: "${focusPrompt}"`);
      sendContextMessage(focusPrompt)
        .then(() => {
          console.log(`✅ [ChatInterface] Focus context sent successfully`);
        })
        .catch((error) => {
          console.error(`❌ [ChatInterface] Error sending focus context:`, error);
        });
    } else {
      console.log(`⚠️ [ChatInterface] No focus prompt available for: ${focusKey}`);
    }
  }, [agenticSettings.focusKey, assistant?.id, isVisible]);

  useEffect(() => {
    assistantRef.current = assistant;
  }, [assistant]);

  useEffect(() => {
    runRef.current = run;
  }, [run]);

  useEffect(() => {
    checkRunStatus();
  }, [run]);

  useEffect(() => {
    if (shouldPoll) {
      intervalRef.current = setInterval(loadRun, 3000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // jk setLoading(shouldPoll);

    // Cleanup interval on unmount or when polling stops
    return () => {
      if (intervalRef.current !== null) {
        // jk  setLoading(false);
        clearInterval(intervalRef.current);
      }
    };
  }, [shouldPoll]);

  /* useEffect(() => {
    if (listening) {
    }
  }, [listening]);
  */

  const getConversationVariables = () => {
    return {
      assistantId: assistantRef?.current?.id,
      threadId: assistantRef?.current?.threads?.[0]?.id,
    };
  };

  const onConversationStateUpdated = (args: any) => {
    // CRITICAL: Skip server loading in insightMode - use initialMessages only
    if (insightMode) {
      console.log(`🚫 [ChatInterface] onConversationStateUpdated: Skipping loadMessages in insightMode`);
      return;
    }
    loadMessages(true);
  };

  const addMessage = (message: string, isAssistant: boolean) => {
    const aiMessage: AiMessage = createStaticMessage(message, isAssistant);
    const ms: AiMessage[] = [];
    ms.push(...messages);
    ms.push(aiMessage);
    setMessages(ms);
  };

  const toggleAvatar = (value: boolean) => {
    setUseAvatar(value);
  };

  const createAssistant = async (forceRecreate?: boolean) => {
    console.log(`🔧 [ChatInterface] createAssistant called - forceRecreate: ${forceRecreate}, hasAssistant: ${!!assistant}, effectiveThreadId: ${effectiveThreadId}, currentMessages: ${messages.length}, insightMode: ${insightMode}`);
    
    // CRITICAL: Skip server calls entirely in insightMode - use initialMessages only
    if (insightMode) {
      console.log(`🚫 [ChatInterface] createAssistant: Skipping in insightMode (using initialMessages only)`);
      setLoading(false);
      return;
    }
    
    if (assistant && !forceRecreate) {
      console.log(`🔧 [ChatInterface] createAssistant returning early - assistant already exists`);
      //  setShouldPoll(true);
      return;
    }

    // If we have an existing threadId, preserve current messages (they were loaded by loadExistingThread)
    const hasExistingThread = !!effectiveThreadId;
    const currentMessages = messages;
    
    console.log(`🔧 [ChatInterface] createAssistant - hasExistingThread: ${hasExistingThread}, currentMessages count: ${currentMessages.length}`);
    
    if (!hasExistingThread) {
      console.log(`🔧 [ChatInterface] createAssistant - clearing messages (no existing thread)`);
      setMessages([getCreatingAssistantMessage()]);
    } else {
      console.log(`🔧 [ChatInterface] createAssistant - preserving ${currentMessages.length} messages (has existing thread)`);
    }
    setLoading(true);

    const currentSettings = AgenticFeatureRegistry.getSettings();
    const appKey = currentSettings.applicationKey || applicationKey || "";
    const assKey = currentSettings.assistantKey || assistantKey || "";
    const featKey = currentSettings.featureKey || "";

    // Build request with optional featureKey
    const assistantRequest: any = {
      applicationKey: appKey,
      assistantKey: assKey,
    };

    // Add featureKey if specified
    if (featKey) {
      assistantRequest.featureKey = featKey;
    }

    console.log(`🔧 [ChatInterface] createAssistant - fetching assistant with:`, assistantRequest);
    const employeeAssistant: AiAssistant =
      await AiService.aiControllerGetAssistant(assistantRequest);

    // If we have an existing threadId, update the assistant's thread to use it
    if (hasExistingThread && employeeAssistant.threads && employeeAssistant.threads.length > 0) {
      console.log(`🔧 [ChatInterface] createAssistant - updating thread from ${employeeAssistant.threads[0].id} to ${effectiveThreadId}`);
      employeeAssistant.threads[0].id = effectiveThreadId;
    }

    setAssistant(employeeAssistant);

    setLoading(false);

    // Only set messages from assistant thread if we don't have an existing thread
    // (existing thread messages were already loaded by loadExistingThread)
    if (!hasExistingThread) {
      const threadMessages = employeeAssistant.threads?.[0]?.messages || [];
      console.log(`🔧 [ChatInterface] createAssistant - setting ${threadMessages.length} messages from assistant thread`);
      setMessages(threadMessages);
      console.log(`📝 Loaded ${threadMessages.length} messages from assistant thread`);
    } else {
      // Preserve the messages that were loaded from the existing thread
      console.log(`🔧 [ChatInterface] createAssistant - preserving ${currentMessages.length} messages from existing thread`);
      console.log(`📝 Preserving ${currentMessages.length} messages from existing thread`);
      setMessages(currentMessages);
    }

    if (employeeAssistant.threads?.[0]?.runs)
      setRun(employeeAssistant.threads?.[0]?.runs?.[0]);
    
    // Check if there's a current focus and send context if available AND component is visible
    // Skip auto-sending focus context on manual clear (forceRecreate) - user explicitly wanted a fresh start
    const focusKey = currentSettings.focusKey;
    if (focusKey && employeeAssistant?.id && !forceRecreate) {
      console.log(`🎯 [ChatInterface] Assistant ready. Checking for focus context: ${focusKey}`);
      
      // ⚠️ Only send context if component is visible!
      if (!isVisible) {
        console.log(`🚫 [ChatInterface] Component is HIDDEN - will send context when user switches to chat tab`);
        return;
      }
      
      const focusPrompt = AgenticFeatureRegistry.getPromptForFocus(focusKey);
      
      if (focusPrompt) {
        console.log(`📤 [ChatInterface] Sending focus context for: ${focusKey}`);
        console.log(`📝 [ChatInterface] Focus prompt: "${focusPrompt}"`);
        // Send context message now that we have an active assistant/thread
        await sendContextMessage(focusPrompt);
        console.log(`✅ [ChatInterface] Focus context sent successfully`);
      } else {
        console.log(`ℹ️ [ChatInterface] No focus prompt available for: ${focusKey}`);
      }
    } else if (forceRecreate) {
      console.log(`🧹 [ChatInterface] Manual clear - skipping auto focus context`);
    } else {
      console.log(`ℹ️ [ChatInterface] No focus context to load (focusKey: ${focusKey})`);
    }
  };

  const handleNewUserMessage = async (newMessage: string, files?: ChatFile[]) => {
    sendMessage(newMessage, files);
  };

  const loadMessages = async (loadAllMessages?: boolean) => {
    // CRITICAL: Skip server loading in insightMode - use initialMessages only
    if (insightMode) {
      console.log(`🚫 [ChatInterface] loadMessages: Skipping in insightMode (using initialMessages only)`);
      return;
    }
    
    const currentAssistant = assistantRef.current;

    if (!currentAssistant?.id) return;

    const threadId = currentAssistant?.threads[0]?.id;
    const currentRun = runRef.current;
    const runId = currentRun?.id;
    const currentMessages: AiMessage[] = [];

    // Only include current messages that belong to the current thread/run
    // This prevents merging old messages after a clear operation
    if (messages && messages.length > 0) {
      const validMessages = messages.filter(msg => {
        // If we have a current runId, only include messages from that run
        if (runId && msg.runId !== runId) {
          return false;
        }
        // Only include messages from the current thread
        if (threadId && msg.threadId !== threadId) {
          return false;
        }
        return true;
      });
      
      if (validMessages.length !== messages.length) {
        console.log(`🚫 [ChatInterface] loadMessages: Filtered out ${messages.length - validMessages.length} messages that don't belong to current thread/run`);
      }
      
      currentMessages.push(...validMessages);
    }

    let runMessages: AiMessage[] = await AiService.aiControllerLoadMessages({
      threadId: threadId,
    });

    runMessages = runMessages.reverse();

    if (!loadAllMessages)
      runMessages = runMessages.filter((message) => message.runId === runId);

    if (currentRun?.messagesPreview) {
      runMessages.push(...currentRun.messagesPreview);
      setMessagesPreview(currentRun?.messagesPreview);
      // cancelRun();
    }

    if (!runMessages || !runMessages.length) return;

    // Filter out internal/context messages from UI display
    const visibleMessages = runMessages.filter((message) => {
      if (!message.metaData) return true;
      try {
        const metadata = JSON.parse(message.metaData);
        return !metadata.internal; // Hide messages marked as internal
      } catch {
        return true; // If can't parse, show it
      }
    });

    // notify new message listeners
    setResponseMessages(getAssistantMessages(visibleMessages));

    // Merge messages intelligently: replace streamed messages with API messages (which have metadata)
    // Create a map of messages by ID for efficient lookup
    const messageMap = new Map<string, AiMessage>();
    
    // First, add all existing messages (user messages, etc.)
    currentMessages.forEach(msg => {
      messageMap.set(msg.id, msg);
    });
    
    // Then, replace/add API messages (which have complete data including metadata)
    visibleMessages.forEach(msg => {
      const existingMsg = messageMap.get(msg.id);
      if (existingMsg) {
        // Message already exists - check if content is duplicated
        const existingText = existingMsg.content?.[0]?.text || '';
        const newText = msg.content?.[0]?.text || '';
        
        // If the new message text is a substring of existing (or vice versa), it's likely a duplicate
        // Replace with API version which has metadata
        if (existingText.includes(newText) || newText.includes(existingText)) {
          console.log(`⚠️ [ChatInterface] Detected potential duplicate message ${msg.id}, replacing with API version`);
          console.log(`   Existing text length: ${existingText.length}, New text length: ${newText.length}`);
        }
      }
      messageMap.set(msg.id, msg);
    });
    
    // Convert back to array, maintaining chronological order
    let mergedMessages = Array.from(messageMap.values()).sort((a, b) => 
      (a.createdAt || 0) - (b.createdAt || 0)
    );
    
    // Deduplicate messages with identical content (same role and text)
    // This prevents duplicate messages that might have different IDs but same content
    const seenContent = new Map<string, AiMessage>();
    const deduplicatedMessages: AiMessage[] = [];
    
    mergedMessages.forEach(msg => {
      const text = msg.content?.[0]?.text || '';
      const contentKey = `${msg.role}:${text.substring(0, 200)}`; // Use first 200 chars as key
      
      if (text.length > 10) { // Only deduplicate substantial messages
        const existing = seenContent.get(contentKey);
        if (existing) {
          // Found duplicate - prefer the one with metadata (from API) or newer timestamp
          const preferExisting = existing.metaData && !msg.metaData;
          const preferNew = msg.metaData && !existing.metaData;
          const preferNewer = !preferExisting && !preferNew && (msg.createdAt || 0) > (existing.createdAt || 0);
          
          if (preferExisting) {
            // Keep existing, skip new
            console.log(`⚠️ [ChatInterface] Deduplicating message ${msg.id} (keeping ${existing.id} with metadata)`);
            return;
          } else if (preferNew || preferNewer) {
            // Replace existing with new
            console.log(`⚠️ [ChatInterface] Deduplicating message ${existing.id} (keeping ${msg.id} with ${preferNew ? 'metadata' : 'newer timestamp'})`);
            const existingIndex = deduplicatedMessages.findIndex(m => m.id === existing.id);
            if (existingIndex >= 0) {
              deduplicatedMessages[existingIndex] = msg;
            }
            seenContent.set(contentKey, msg);
            return;
          } else {
            // Keep existing, skip new
            console.log(`⚠️ [ChatInterface] Deduplicating message ${msg.id} (keeping ${existing.id})`);
            return;
          }
        }
        seenContent.set(contentKey, msg);
      }
      
      deduplicatedMessages.push(msg);
    });
    
    // Log if deduplication removed any messages
    if (deduplicatedMessages.length < mergedMessages.length) {
      console.log(`✅ [ChatInterface] Deduplicated ${mergedMessages.length - deduplicatedMessages.length} duplicate messages`);
    }
    
    mergedMessages = deduplicatedMessages;
    
    console.log(`📊 [ChatInterface] Merged ${mergedMessages.length} messages (${currentMessages.length} existing + ${visibleMessages.length} from API)`);
    
    // Log metadata presence for assistant messages
    mergedMessages.forEach(msg => {
      if (msg.role === 'assistant' && msg.metaData) {
        try {
          const metadata = JSON.parse(msg.metaData);
          if (metadata.intelligenceCard) {
            console.log(`📊 [ChatInterface] Message ${msg.id} has intelligenceCard metadata:`, {
              headline: !!metadata.intelligenceCard.headline,
              byline: !!metadata.intelligenceCard.byline,
              headlineValue: metadata.intelligenceCard.headline,
              bylineValue: metadata.intelligenceCard.byline,
            });
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    });
    
    setMessages(mergedMessages);
  };

  const getAssistantMessages = (m: AiMessage[]): AiMessage[] => {
    // get messages where the role = assistant
    const messages = m.filter((message) => message.role === "assistant");

    return messages;
  };

  const cancelRun = () => {
    onStop();
    // setRun(undefined);
  };

  const isRunning = (): boolean => {
    const currentRun = runRef.current;
    let running: boolean = false;

    if (!currentRun) {
      running = false;
    } else {
      running =
        currentRun?.status === "queued" || currentRun?.status === "in_progress";
    }

    return running;
  };

  const checkRunStatus = () => {
    // CRITICAL: Disable polling in insightMode - we're just displaying existing messages
    if (insightMode) {
      setShouldPoll(false);
      return;
    }
    
    const running = isRunning();

    // jk setLoading(running);
    setShouldPoll(running);
  };

  const loadRun = async () => {
    // CRITICAL: Skip server loading in insightMode - use initialMessages only
    if (insightMode) {
      console.log(`🚫 [ChatInterface] loadRun: Skipping in insightMode (using initialMessages only)`);
      return;
    }
    
    const currentAssistant = assistantRef.current;
    if (!currentAssistant?.id) return;

    const currentRun = runRef.current;

    if (!currentRun) {
      return;
    }

    const aiRun: AiRun = await AiService.aiControllerLoadRun({
      runId: currentRun.id,
      threadId: currentRun.threadId,
    });

    if (!currentRun.messagesPreview || aiRun.status === "completed") {
      setRun(aiRun);
    }

    loadMessages();
  };

  const onStop = async () => {
    const currentAssistant = assistantRef.current;
    if (!currentAssistant?.id) return;

    const currentRun = runRef.current;

    if (!currentRun) {
      return;
    }

    try {
      await AiService.aiControllerCancelRun({
        runId: currentRun.id,
        threadId: currentRun.threadId,
      });
    } catch (error) {
      console.error("error canceling run", error);
    }

    setRun(undefined);
  };

  const onClear = async () => {
    // CRITICAL: In insightMode, don't clear or create new assistants - we're read-only
    if (insightMode) {
      console.log(`🚫 [ChatInterface] onClear: Skipping in insightMode (read-only mode)`);
      return;
    }
    
    // Notify feature providers
    AgenticFeatureRegistry.notifyConversationClear();
    
    // Clear ALL state FIRST (before createAssistant so saveState doesn't cache old messages)
    setMessages([]);
    setResponseMessages([]);
    setMessagesPreview([]);
    setRun(undefined);
    setAssistant(undefined); // Clear assistant so createAssistant starts fresh
    setEntityCardsByMessage(new Map());
    clearCustomers();
    clearProducts();
    
    // Clear WebSocket accumulated messages (important - these persist across conversations!)
    clearSocketMessages();
    
    // Clear sessionStorage to prevent old messages from being restored
    sessionStorage.removeItem("chatInterfaceState");
    
    // CRITICAL: Clear thread loading refs to prevent reloading old threads after clear
    lastLoadedThreadIdRef.current = null;
    isLoadingThreadRef.current = false;
    
    console.log('🧹 Cleared all messages, socket state, cache, and thread loading refs');
    
    // Now create new assistant (which will create a fresh thread with no messages)
    createAssistant(true);
  };

  /*  const onToggleListening = async () => {
    setListening(!listening);
  };
*/
  const clearCustomers = async () => {
    setCustomers(new Map());
  };

  const clearProducts = async () => {
    setProducts(new Map());
  };

  const sendMessage = async (prompt: string, chatFiles?: ChatFile[]) => {
    const currentAssistant = assistantRef.current;

    if (!currentAssistant?.id) return "";

    // Notify feature providers about the prompt
    AgenticFeatureRegistry.notifyPromptSent(prompt);

    // Process files - convert images to base64, upload documents
    let uploadedFileReferences: any[] = [];
    if (chatFiles && chatFiles.length > 0) {
      console.log(`📤 Processing ${chatFiles.length} file(s)...`);
      setLoading(true);
      
      for (const chatFile of chatFiles) {
        try {
          const isImage = chatFile.type === 'image';
          
          if (isImage) {
            // Convert image to base64 data URL (no server upload needed!)
            console.log(`🖼️ Converting image to base64: ${chatFile.file.name}`);
            
            const base64Url = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(chatFile.file);
            });
            
            uploadedFileReferences.push({
              id: chatFile.id,
              externalId: chatFile.id,
              name: chatFile.file.name,
              fileExtension: chatFile.file.name.split('.').pop(),
              url: base64Url, // Base64 data URL for Vision API
            });
            
            console.log(`✅ Image converted: ${chatFile.file.name} (${(base64Url.length / 1024).toFixed(1)} KB)`);
          } else {
            // Upload non-image files to server
            console.log(`📄 Uploading document: ${chatFile.file.name}`);
            
            const formData = new FormData();
            formData.append('file', chatFile.file);
            
            const uploadedFile = await apiClient.uploadFile('/ai/createFile', formData);
            console.log(`✅ Uploaded document: ${chatFile.file.name}`, uploadedFile);
            
            uploadedFileReferences.push({
              id: uploadedFile.id,
              externalId: uploadedFile.externalId,
              name: uploadedFile.name,
              fileExtension: uploadedFile.fileExtension,
              url: undefined, // No URL for documents
            });
          }
        } catch (error) {
          console.error(`❌ Error processing file ${chatFile.file.name}:`, error);
          console.error(`❌ Error details:`, {
            message: error.message,
            stack: error.stack,
            name: error.name,
          });
          alert(`Failed to process ${chatFile.file.name}: ${error.message}`);
        }
      }
      
      setLoading(false);
      console.log(`✅ All files processed. References:`, uploadedFileReferences);
    }

    // Build message content - text plus image files
    const messageContents: AiMessageContent[] = [];
    
    // Add text content
    if (prompt.trim()) {
      messageContents.push({
        fileReferences: [],
        text: prompt,
        type: "text",
      });
    }
    
    // Add image file content for images (for vision API)
    const imageFiles = uploadedFileReferences.filter(ref => 
      ref.fileExtension && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ref.fileExtension.toLowerCase())
    );
    
    if (imageFiles.length > 0) {
      imageFiles.forEach(imgRef => {
        messageContents.push({
          type: "image_file",
          text: "",
          fileReferences: [imgRef],
        });
      });
    }

    const aiMessage: AiMessage = {
      assistantId: "",
      content: messageContents,
      createdAt: Date.now(),
      fileReferences: uploadedFileReferences, // All files at message level
      id: `user_msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Generate unique ID for user messages
      metaData: "",
      parentAssistantId: currentAssistant?.id,
      personId: "",
      role: "user", // Set correct role for user messages
      runId: "",
      threadId: currentAssistant.threads[0].id,
    };

    // Don't create empty placeholder - streamMessage() will create the assistant response when it arrives
    setMessages(messages.concat(aiMessage));

    try {
      const aiRun: AiRun = await AiService.aiControllerSendMessage(aiMessage);
      // JK TODO clean up post NRF - caching logic with delay
      if (aiRun.messagesPreview) {
        setLoading(true);
        const newMessages: AiMessage[] = [];
        newMessages.push(aiMessage);
        newMessages.push(...aiRun.messagesPreview);
        const setMessagesDelayed = () => {
          setLoading(false);
          setMessages(newMessages);
        };
        setTimeout(setMessagesDelayed, 700);
      }
      setRun(aiRun);
    } catch (error) {
      // jk setLoading(false);
      console.error("error sending message", error);
    }
  };

  /**
   * Send a context message to the AI without showing it in the chat UI
   * Used for setting context like "we're now working with dashboard X"
   */
  const sendContextMessage = async (contextPrompt: string) => {
    const currentAssistant = assistantRef.current;

    if (!currentAssistant?.id) return "";

    console.log('🔧 [ChatInterface] Sending context message (hidden from UI)');
    console.log(`📤 [ChatInterface] Context prompt: "${contextPrompt}"`);

    const content: AiMessageContent = {
      fileReferences: [],
      text: contextPrompt,
      type: "text",
    };

    const aiMessage: AiMessage = {
      assistantId: "",
      content: [content],
      createdAt: Date.now(),
      fileReferences: [],
      id: `ctx_msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Generate unique ID for context messages
      metaData: JSON.stringify({ internal: true, type: 'context' }), // Mark as internal
      parentAssistantId: currentAssistant?.id,
      personId: "",
      role: "user", // Context messages are user messages to the AI
      runId: "",
      threadId: currentAssistant.threads[0].id,
    };

    // Don't add to visible messages - send directly to AI
    try {
      setLoading(true);
      console.log('⏳ [ChatInterface] Sending to AI service...');
      const aiRun: AiRun = await AiService.aiControllerSendMessage(aiMessage);
      setRun(aiRun);
      
      // Load the response but don't show the context prompt in UI
      console.log('📥 [ChatInterface] Loading AI response...');
      await loadMessages(false);
      setLoading(false);
      
      console.log('✅ [ChatInterface] Context message processed - AI has loaded context');
    } catch (error) {
      setLoading(false);
      console.error("❌ [ChatInterface] Error sending context message:", error);
    }
  };

  const loadCustomerDetails = async (id: string) => {
    if (customers.get(id)) {
      return;
    }

    const customer: CustomerReference =
      await AiService.aiControllerGetCustomerInsights(id);

    customers.set(customer.id, customer);
    setCustomers(new Map([...customers]));
  };

  const loadProductDetails = async (id: string) => {
    if (products.get(id)) {
      return;
    }

    const product: ProductDto =
      await CatalogService.catalogControllerGetProductByProductId(id);

    products.set(product.id, product);

    setProducts(new Map([...products]));
  };

  const getCreatingAssistantMessage = (): AiMessage => {
    return createStaticMessage("Creating Your Assistant...", true);
  };

  const createStaticMessage = (
    text: string,
    isAssistant: boolean
  ): AiMessage => {
    // Generate unique ID using timestamp and random suffix
    const id: string = `staticMessage_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const role = isAssistant ? "assistant" : "user";
    return {
      assistantId: "assistant",
      content: [
        {
          fileReferences: [],
          text: text,
          type: "text",
        },
      ],
      createdAt: Date.now(),
      fileReferences: [],
      id: id,
      metaData: "",
      parentAssistantId: "",
      personId: "",
      role: role,
      runId: "",
      threadId: "",
    };
  };

  const getPromptToggles = () => {
    return (
      <div className="toggle-button-group">
        <button
          className={`toggle-button ${useAvatar ? "" : "active"}`}
          onClick={() => toggleAvatar(false)}
        >
          Text
        </button>
        <button
          className={`toggle-button ${useAvatar ? "active" : ""}`}
          onClick={() => toggleAvatar(true)}
        >
          Avatar
        </button>
      </div>
    );
  };

  const getChatHeaderControlBar = () => {
    const iconButtonStyle = {
      marginLeft: '10px',
      background: 'linear-gradient(145deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      borderRadius: '12px',
      boxShadow: '0 0 10px rgba(255, 255, 255, 0.3)',
      color: theme.palette.primary.main,
      transition: 'background 0.3s ease, transform 0.2s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.2)',
        transform: 'scale(1.1)',
      },
    };

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: '100%',
          px: 2,
          py: 1,
          mb: 1,
          borderRadius: 0,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          backgroundColor: 'rgba(0, 0, 0, 0.2)', // darker glass effect
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: 'inset 0 -1px 0 rgba(255,255,255,0.05), 0 2px 8px rgba(0, 0, 0, 0.3)',
          zIndex: 10,
        }}
      >
        {voiceMode === 'realtime' && assistant?.id ? (
          <VoiceIORealtime
            assistantId={assistant.id}
            voice="alloy"
            enableFunctions={true}
            onTranscript={(text) => {
              console.log('Realtime transcript:', text);
            }}
          />
        ) : (
          <VoiceIO
            loading={loading}
            onSendMessage={handleNewUserMessage}
            onStop={onStop}
            responseMessages={responseMessages}
          />
        )}
        <IconButton
          sx={iconButtonStyle}
          onClick={handleSettingsOpen}
          title="Settings"
        >
          <SettingsIcon />
        </IconButton>
        <IconButton
          sx={iconButtonStyle}
          onClick={onClear}
          title="Clear Conversation"
        >
          <ClearIcon />
        </IconButton>
        <IconButton
          sx={{
            ...iconButtonStyle,
            background: voiceMode === 'realtime'
              ? 'linear-gradient(145deg, rgba(255,200,0,0.15), rgba(255,165,0,0.05))'
              : iconButtonStyle.background,
          }}
          onClick={() => setVoiceMode(voiceMode === 'realtime' ? 'traditional' : 'realtime')}
          title={voiceMode === 'realtime' 
            ? 'Voice: Realtime (Fast ⚡) - Click for Traditional' 
            : 'Voice: Traditional (Stable) - Click for Realtime ⚡'}
        >
          <FlashOnIcon 
            sx={{
              color: voiceMode === 'realtime' 
                ? theme.palette.warning.main 
                : 'rgba(255, 255, 255, 0.4)'
            }}
          />
        </IconButton>
      </Box>
    );
  };

  const actions: Action[] = [
    { id: "action_rememberSalesTrends", label: "Sales Trends" },
    { id: "action_rememberMostActiveItems", label: "Active Items" },
    { id: "action_rememberMostActiveCustomers", label: "Active Customers" },
    { id: "action_rememberLocationPerformance", label: "Location Performance" },
    { id: "action_rememberLoyaltyEnrollment", label: "Loyalty Enrollment" },
  ];

  const onAction = (id: string) => {
    sendMessage(id);
  };

  const handleSettingsOpen = () => {
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

  const handleSettingsChange = (newSettings: AgenticSettings) => {
    setAgenticSettings(newSettings);
    
    // CRITICAL: Don't recreate assistant in insightMode - we're read-only
    if (insightMode) {
      console.log(`🚫 [ChatInterface] handleSettingsChange: Skipping assistant recreation in insightMode`);
      return;
    }
    
    // If any key settings changed, recreate the assistant
    const currentSettings = AgenticFeatureRegistry.getSettings();
    if (newSettings.agentId !== currentSettings.agentId || 
        newSettings.applicationKey !== currentSettings.applicationKey ||
        newSettings.assistantKey !== currentSettings.assistantKey ||
        newSettings.featureKey !== currentSettings.featureKey) {
      createAssistant(true);
    }
  };

  // Determine variant-specific styles
  const variantStyles = {
    default: {},
    insight: {
      backgroundColor: effectiveDisplayConfig.backgroundColor || '#000000',
    },
    embedded: {
      backgroundColor: effectiveDisplayConfig.backgroundColor || 'transparent',
    },
  };
  
  const containerStyle = {
    ...variantStyles[effectiveDisplayConfig.variant || 'default'],
    ...(effectiveDisplayConfig.backgroundColor ? { backgroundColor: effectiveDisplayConfig.backgroundColor } : {}),
  };

  return (
    <div 
      className={`chat-interface chat-variant-${effectiveDisplayConfig.variant}`} 
      ref={componentRef}
      style={containerStyle}
    >
      <div className="topSpacer" />

      <div className={`chat-body ${isDrawer ? "drawer-mode" : "wide-mode"}`}>
        {/* Custom header takes precedence */}
        {effectiveDisplayConfig.customHeader ? (
          <div className="chat-custom-header">{effectiveDisplayConfig.customHeader}</div>
        ) : effectiveDisplayConfig.showToolbar ? (
          <div className="chat-controls">{getChatHeaderControlBar()}</div>
        ) : effectiveDisplayConfig.title ? (
          /* Simple title bar when no toolbar but title is provided */
          <div className="chat-title-bar" style={{
            padding: '12px 16px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            backgroundColor: 'rgba(0,0,0,0.3)',
          }}>
            <span style={{ 
              fontSize: '16px', 
              fontWeight: 600, 
              color: '#fff' 
            }}>
              {effectiveDisplayConfig.title}
            </span>
          </div>
        ) : null}

        {/* answer */}
        {/* Render AnswerSection normally */}
        <div className="answer-section">
          <AnswerSection
            customers={customers}
            formatActions={formatActions}
            loading={loading}
            messages={messages}
            products={products}
            runStatus={runStatus}
            isDrawer={isDrawer}
            entityCardsByMessage={entityCardsByMessage}
            insightMode={insightMode}
            isVisible={isVisible}
          />
        </div>

        {/* prompt - controlled by displayConfig */}
        {effectiveDisplayConfig.showPromptSection && !useAvatar && (
          <div className="prompt-section">
            <PromptSection
              loading={loading}
              onSendMessage={handleNewUserMessage}
              onStop={onStop}
            />
          </div>
        )}
      </div>

      {/* Settings Dialog */}
      <SettingsDialog
        open={settingsOpen}
        onClose={handleSettingsClose}
        onSettingsChange={handleSettingsChange}
      />
    </div>
  );
};

export default ChatInterface;